import type { GetServerSideProps } from 'next';
import type { FrontPage } from 'lib/queries/pages';

import React, { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

import Blocks from 'components/blocks';
import PageWrap from 'components/page-wrap';
import PresenceJson from 'components/structured-data/presence';
import { getFrontPage } from 'lib/queries/pages';
import { getPostHogServerClient } from 'lib/posthog-server';
import { STATE_COOKIE_NAME } from 'lib/cookies/state-cookie';
import HomeHeroVariant from 'components/hero-homepage-variant';
import HomeHeroNew from 'components/hero-homepage-new';

function readStateCookie(cookieHeader = ''): string | undefined {
  const match = cookieHeader.match(
    new RegExp('(?:^|; )' + STATE_COOKIE_NAME + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

const Home: React.FC<FrontPage> = ({
  page: {
    title,
    seo,
    acf: { pageType, blocks, hero },
  },
  variant,
  featureFlag,
  initialStateCode,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      /*       posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      }); */
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);

  return (
    <PageWrap seo={seo} title={title} pageType="home">
      <PresenceJson />
      {variant === 'test' ? (
        <HomeHeroNew {...hero} initialStateCode={initialStateCode} />
      ) : (
        <HomeHeroVariant {...hero} />
      )}
      <Blocks blocks={blocks} pageType={pageType} />
    </PageWrap>
  );
};

export const getServerSideProps = (async context => {
  const { req, query } = context;

  const distinctId = req.headers['x-posthog-distinct-id'] as string;
  const flagKey = 'website-hero-benefits-block-homepage';

  console.log('cookies', req.headers.cookie);

  const queryState = typeof query.state === 'string' ? query.state : undefined;
  const initialStateCode =
    (queryState || readStateCookie(req.headers.cookie))?.toUpperCase() ?? null;

  console.log('initialStateCode', initialStateCode);

  let variant: string | null = null;

  try {
    const posthog = getPostHogServerClient();
    const flags = await posthog.evaluateFlags(distinctId, {
      personProperties: { $pathname: '/' },
    });

    const flagValue = flags.getFlag(flagKey) as string | undefined;

    if (flagValue) {
      variant = flagValue;
    } else {
      console.warn(
        `[PostHog] Flag "${flagKey}" returned undefined for ${distinctId} — excluding from experiment`,
      );
    }
  } catch (error) {
    console.warn(
      `[PostHog] evaluateFlags failed for ${distinctId}:`,
      error?.message || error,
      '— excluding from experiment',
    );
  }

  return await getFrontPage({
    ...context,
    variant: variant as 'test' | 'control' | null,
    featureFlag: variant ? flagKey : null,
    initialStateCode,
    posthogBootstrap: {
      distinctID: distinctId,
      featureFlags: variant ? { [flagKey]: variant } : {},
    },
  });
}) satisfies GetServerSideProps<FrontPage>;

export default Home;
